import { inject, Injectable } from '@angular/core';
import { CepStateService, FlashMessageService } from '@libs/shared/common';
import { CatalogSearchResultModel} from '@libs/shop/models';
import { ScannerHandlerControlsModel, ScannerHeaderConfig, ScannerScanResult } from '@libs/shared/models';
import { catchError, defer, from, Observable, of, switchMap } from 'rxjs';
import { map } from 'rxjs/operators';
import { CatalogSearchRepositoryService } from '../catalog-search';
import { ScannerHandlerBase } from '../scanner-handler';
import { ScannerHandler } from '../scanner-handler/scanner-handler.decorator';
import { ShoppingListsState } from '../shopping-lists';

@Injectable({
  providedIn: 'root',
})
@ScannerHandler()
export class ShoppingListScannerHandlerService extends ScannerHandlerBase {
  public static override readonly Key: string = 'SHOPPING_LIST_SCANNER';

  private readonly cepStateService: CepStateService = inject(CepStateService);
  private readonly searchService: CatalogSearchRepositoryService = inject(CatalogSearchRepositoryService);
  private readonly shoppingListsState: ShoppingListsState = inject(ShoppingListsState);
  private readonly flashMessageService: FlashMessageService = inject(FlashMessageService);

  public override handleOrderModeEnabledScan(value: string, controls: ScannerHandlerControlsModel, relatedResourceId: string): Observable<void> {
    return defer(() => {
      controls.showLoader();

      return this.addToShoppingListIfSingleResultIsFound(value, relatedResourceId).pipe(switchMap((result) => {
          controls.hideLoader();

          switch (result) {
            case 'success':
              return controls.showSuccess();
            case 'notFound':
              this.flashMessageService.showMessage('errorScannerProductNotFound');

              return controls.showError();
            case 'multipleFound':
              return this.navigateToSearch(value);
          }
        }),
        catchError((err) => {
          this.flashMessageService.showMessage('errorSearchResult');
          console.error(err);

          return of(undefined);
        }));
    });
  }

  public override handleOrderModeDisabledScan(value: string): Observable<void> {
    return this.navigateToSearch(value);
  }

  public override handleMultiScan(value: string): Observable<void> {
    return this.navigateToSearch(value);
  }

  public override getHeaderConfig(relatedResourceId: string): ScannerHeaderConfig {
    return {
      title: 'Artikel Scannen',
      iconTitle: 'Einkaufsliste',
      icon: 'list-tree',
      iconStyle: 'light',
      link: '/shopping-lists/' + relatedResourceId,
    };
  }

  private navigateToSearch(searchText: string): Observable<void> {
    return from(this.cepStateService.navigateToSearch(searchText)).pipe(
      map(() => { /* empty */
      }));
  }

  private addToShoppingListIfSingleResultIsFound(code: string, listId: string): Observable<ScannerScanResult> {
    return this.searchService.search({ q: code }).pipe(switchMap((searchResult: CatalogSearchResultModel) => {
      if (!searchResult.products.length) {
        return of('notFound' as ScannerScanResult);
      }

      if (searchResult.products.length > 1) {
        return of('multipleFound' as ScannerScanResult);
      }

      return from(this.shoppingListsState.addProductToShoppingList({
        listId: listId,
        sku: searchResult.products[0].sku,
        quantity: 1,
      })).pipe(map((): ScannerScanResult => 'success'));
    }));
  }
}
